/**
 * Taipei Toy Festival pre-orders — browser-side mirror of api/_ttf.cjs, used
 * only for display (admin price preview, countdown, order summary, CLABE
 * hint). The API is authoritative and recomputes everything on submit.
 */
// While true, /pedidos/taipei-toy-festival shows only the title and the price
// calculator — catalog, order form, countdown, conditions and request form are
// hidden. Set to false to bring the full ordering page back. (UI only: the
// order/request API endpoints are unaffected.)
export const TTF_CALCULATOR_ONLY = true;

export const TTF_MARKUP = 0.45; // incl. the cash withdrawal in Taiwan
export const TTF_EXCHANGE_RATE = 0.57; // MXN per 1 TWD — fixed
export const isTtfTransfer = (method) => method === 'bank_transfer_full';
export const TTF_DELIVERY_FEE_MXN = 200;
export const TTF_ORDER_DEADLINE = '2026-10-10T12:00:00+08:00'; // Saturday noon, Taipei (UTC+8)

export function computeTtfPriceMxn(originalPrice) {
  if (originalPrice === '' || originalPrice === null || originalPrice === undefined) return null;
  const raw = Number(originalPrice) * TTF_EXCHANGE_RATE * (1 + TTF_MARKUP);
  if (!Number.isFinite(raw) || raw <= 0) return null;
  // Next 100 MXN; cents first so float noise can't bump an exact multiple.
  return Math.ceil(Math.round(raw * 100) / 10000) * 100;
}

export function isValidClabe(value) {
  const clabe = String(value || '').replace(/\s+/g, '');
  if (!/^\d{18}$/.test(clabe)) return false;
  const weights = [3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 17; i++) sum += (Number(clabe[i]) * weights[i % 3]) % 10;
  return (10 - (sum % 10)) % 10 === Number(clabe[17]);
}

const toCents = (n) => Math.round(n * 100) / 100;

export function computeTtfOrderAmounts(lines, deliveryMethod, paymentMethod) {
  let subtotal = 0;
  let lotterySubtotal = 0;
  for (const line of lines) {
    const amount = Number(line.price) * Number(line.quantity);
    subtotal += amount;
    if (line.is_lottery) lotterySubtotal += amount;
  }
  const deliveryFee = deliveryMethod === 'delivery' ? TTF_DELIVERY_FEE_MXN : 0;
  const nonLotterySubtotal = subtotal - lotterySubtotal;
  // Same price whichever way the customer pays (the transfer discount was
  // removed on 2026-10-08), always 100% up front.
  const payableNow = nonLotterySubtotal > 0 ? toCents(nonLotterySubtotal + deliveryFee) : 0;
  const dueNow = payableNow;
  const total = toCents(subtotal + deliveryFee);
  return { subtotal, lotterySubtotal, deliveryFee, total, payableNow, dueNow, dueLater: toCents(total - dueNow) };
}

/** Milliseconds → { days, hours, minutes, seconds }, clamped at zero. */
export function splitDuration(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
}

/**
 * Digits for a wa.me link. Customers usually type a 10-digit Mexican number
 * without the country code, which wa.me needs — prefix 52 in that case.
 */
export function toWhatsAppNumber(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 10 ? `52${digits}` : digits;
}

/**
 * MXN for the Taipei Toy Festival customer page: whole amounts without
 * ".00" ($800); any real cents are kept ($806.40), so the customer never
 * sees a rounded amount to transfer.
 */
export function formatTtfMxn(amount, locale = 'es-MX') {
  const value = Number(amount) || 0;
  const whole = Math.round(value * 100) % 100 === 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(value);
}
